import React, { Component } from 'react';
import propTypes from 'prop-types';
import { DropTarget } from 'react-dnd';
import { STICKER_DROP } from '~/Config/DragTypes';
import { MONTH_VIEW } from '~/Config/CalendarViews';
import StickerDrag from '~/Components/Global/Stickers/StickerDrag';

import './StickerDrop.scss';

const STICKER_SCALE = 0.05;

const eventTarget = {
  drop: (props, monitor, component) => {
    const parentPosition = component.stickerDropRef.getBoundingClientRect();
    const stickerPosition = monitor.getClientOffset();
    const sticker = monitor.getItem().sticker;

    const initialClientOffset = monitor.getInitialClientOffset();
    const initialSourceClientOffset = monitor.getInitialSourceClientOffset();

    if (!initialClientOffset || !initialSourceClientOffset) {
      console.error('Drag event initialization error: Initial offsets are null');
      return;
    }

    const stickerBoundingRect = monitor.getItem().stickerRef.getBoundingClientRect();
    let scale = sticker.scale;
    if (scale === undefined) {
      scale = 1;
    }
    const offsetX = monitor.getInitialClientOffset().x - monitor.getInitialSourceClientOffset().x;
    const offsetY = monitor.getInitialClientOffset().y - monitor.getInitialSourceClientOffset().y;
    let position_left = 0,
      position_right = 0,
      position_top = 0,
      position_bottom = 0,
      center_x = 0,
      center_y = 0;

    const originX = stickerPosition.x - offsetX - parentPosition.left;
    position_right =
      ((parentPosition.width - originX - monitor.getItem().stickerRef.offsetWidth * scale) /
        parentPosition.width) *
      100;
    position_left = (originX / parentPosition.width) * 100;

    const originY = stickerPosition.y - offsetY - parentPosition.top;
    position_bottom =
      ((parentPosition.height - originY - monitor.getItem().stickerRef.offsetHeight * scale) /
        parentPosition.height) *
      100;
    position_top = (originY / parentPosition.height) * 100;

    center_x =
      ((stickerPosition.x -
        offsetX +
        monitor.getItem().stickerRef.offsetWidth * 0.5 * scale -
        (parentPosition.left + parentPosition.width * 0.5)) /
        parentPosition.width) *
      100;
    center_y =
      ((stickerPosition.y -
        offsetY +
        monitor.getItem().stickerRef.offsetHeight * 0.5 * scale -
        (parentPosition.top + parentPosition.height * 0.5)) /
        parentPosition.height) *
      100;

    // need to adjust the scale of version 1 stickers...they were always 50px at scale 1
    if (sticker.position_version == 1) {
      if (window.innerWidth < 1024) {
        // tablet
        const baseSize =
          (window.innerWidth - 180 - (window.innerWidth * 0.5 - 70) * 0.25) * STICKER_SCALE;
        const currentSize = monitor.getItem().stickerRef.offsetWidth * scale;
        scale = currentSize / baseSize;
      } else {
        const baseSize =
          (window.innerWidth - 340 - (window.innerWidth * 0.5 - 160) * 0.25) * STICKER_SCALE;
        const currentSize = monitor.getItem().stickerRef.offsetWidth * scale;
        scale = currentSize / baseSize;
      }
    }

    if (monitor.getItem().sticker.date) {
      props.moveSticker(
        {
          ...monitor.getItem().sticker,
          position_left,
          position_top,
          position_right,
          position_bottom,
          center_x,
          center_y,
          position_version: props.view == MONTH_VIEW ? 3 : 2,
          scale,
        },
        props.date,
        props.view
      );
    } else {
      props.dropSticker(
        {
          ...monitor.getItem().sticker,
          position_left,
          position_top,
          position_right,
          position_bottom,
          center_x,
          center_y,
          position_version: props.view == MONTH_VIEW ? 3 : 2,
        },
        props.date,
        props.view
      );
    }
  },
};

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  didDrop: monitor.didDrop(),
  isOverCurrent: monitor.isOver({ shallow: true }),
});

@DropTarget(STICKER_DROP, eventTarget, dropCollect)
export default class StickerDrop extends Component {
  constructor() {
    super();
    this.stickerDropRef = false;
  }
  reduceRotation = rotation => {
    if (rotation <= 360 && rotation >= -360) {
      return rotation;
    } else {
      const div = Math.floor(rotation / 360);
      const sub = div * 360;
      const difference = rotation - sub;
      return difference;
    }
  };

  rotateSticker = (e, sticker) => {
    const { onChangeStart, onChangeEnd } = this.props;
    onChangeStart();
    const stickerImage = document.querySelector(`#sticker-container-${sticker.id} img`);
    const stickerActions = document.querySelector(
      `#sticker-container-${sticker.id} .sticker-actions`
    );
    const icon = document.getElementById(`icon-${sticker.id}`);
    let currentRotation = sticker.rotation;
    const localSaveFunction = this.props.resizeSticker;
    const localConcurrentFunction = this.reduceRotation;
    const bcr = stickerImage.getBoundingClientRect();
    const centerX = bcr.left + bcr.width / 2;
    const centerY = bcr.top + bcr.height / 2;
    let startAngle = Math.atan2(
      icon.getBoundingClientRect().y - centerY,
      icon.getBoundingClientRect().x - centerX
    );

    window.addEventListener('mousemove', mousemove);
    window.addEventListener('mouseup', mouseup);

    function mousemove(e) {
      const dragEndAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
      const angleDifference = ((dragEndAngle - startAngle) * 180) / Math.PI;
      const withCurrentRotation = currentRotation + angleDifference;
      const reducedWithCurrent = localConcurrentFunction(withCurrentRotation);

      stickerImage.style.transform = `rotate(${reducedWithCurrent}deg)`;
      if (stickerActions) stickerActions.style.transform = `rotate(${reducedWithCurrent}deg)`;
      startAngle = dragEndAngle;
      currentRotation += angleDifference;
    }
    function mouseup(e) {
      setTimeout(() => onChangeEnd());
      localSaveFunction(
        { ...sticker, rotation: localConcurrentFunction(currentRotation) },
        sticker.date
      );
      window.removeEventListener('mousemove', mousemove);
      window.removeEventListener('mouseup', mouseup);
    }
  };

  handleDelete = (e, sticker) => {
    e.stopPropagation();
    e.preventDefault();
    this.props.deleteSticker(sticker);
  };

  renderStickers = (stickers, date) => {
    const currentStickers = stickers[date];
    if (!currentStickers) return false;
    return currentStickers.map((sticker, index) => {
      if (sticker.calendarViewName !== this.props.view) return false;
      let style = {
        maxWidth: 'auto',
        maxHeight: 'auto',
        transform: `scale(${!sticker.scale ? 1 : sticker.scale}, ${
          !sticker.scale ? 1 : sticker.scale
        })`,
      };
      let useCenterX = false,
        useCenterY = false;
      if (sticker.position_version > 1) {
        if (sticker.position_right < sticker.position_left)
          style = { ...style, right: sticker.position_right + '%' };
        if (sticker.position_bottom < sticker.position_top)
          style = { ...style, bottom: sticker.position_bottom + '%' };
      }
      if (sticker.position_version > 2) {
        if (
          Math.abs(sticker.center_x) <
          Math.min(Math.abs(sticker.position_left), Math.abs(sticker.position_right)) * 5
        ) {
          style = {
            ...style,
            left: sticker.center_x + 50 + '%',
            transform: `translateX(-50%) ${style.transform}`,
            right: null,
          };
          useCenterX = true;
        }
        if (
          Math.abs(sticker.center_y) <
          Math.min(Math.abs(sticker.position_top), Math.abs(sticker.position_bottom)) * 5
        ) {
          style = {
            ...style,
            top: sticker.center_y + 50 + '%',
            transform: `translateY(-50%) ${style.transform}`,
            bottom: null,
          };
          useCenterY = true;
        }
      }
      if (!style.right && !useCenterX) style = { ...style, left: sticker.position_left + '%' };
      if (!style.bottom && !useCenterY) style = { ...style, top: sticker.position_top + '%' };

      return sticker.temp ? (
        <div className="sticker temp" key={index} style={style}>
          <img className="sticker-image" src={sticker.url} />
        </div>
      ) : (
        <StickerDrag
          handleDelete={e => this.handleDelete(e, sticker)}
          key={index}
          sticker={sticker}
          rotateSticker={this.rotateSticker}
          resizeSticker={this.props.resizeSticker}
        />
      );
    });
  };

  render() {
    const { children, stickers, date, connectDropTarget } = this.props;
    return connectDropTarget(
      <div ref={r => (this.stickerDropRef = r)} className="sticker-drop-wrapper">
        {this.renderStickers(stickers, date)}
        {children}
      </div>
    );
  }
}

StickerDrop.propTypes = {
  isOver: propTypes.bool,
  stickers: propTypes.oneOfType([propTypes.object, propTypes.bool]),
  children: propTypes.node,
  connectDropTarget: propTypes.func,
  deleteSticker: propTypes.func,
  date: propTypes.string,
  view: propTypes.string,
  resizeSticker: propTypes.any,
  rotateSticker: propTypes.any,
  allowOutside: propTypes.bool,
  onChangeStart: propTypes.func.isRequired,
  onChangeEnd: propTypes.func.isRequired,
};

StickerDrop.defaultProps = {
  showLabel: false,
};
